import React from "react";
import { useSelector } from "react-redux";
import { MetaTags } from "react-meta-tags";
import {
  JsonLd,
  buildPathHreflang,
  buildStaticPageJsonLd,
  getAbsoluteUrl,
  langPrefix,
  ogLocale,
} from "../../helpers/seoMeta";
import { getStaticPageSeo } from "../../helpers/staticPagesSeo";

/**
 * Reusable SEO for static and dynamic listing pages.
 * @param {string} [pageKey] - key from STATIC_PAGES_SEO
 * @param {object} [override] - title, description, ogImage, path, schemaType,
 *   faqItems, breadcrumbs, itemList, brandName, noindex, canonicalPath
 */
export default function PageSeo({ pageKey, override = {} }) {
  const { currentLanguage } = useSelector(({ main }) => main);
  const base = pageKey ? getStaticPageSeo(pageKey, currentLanguage) : null;
  if (!base && !override.title) return null;

  const title = override.title || base?.title;
  const description = override.description || base?.description;
  const path = override.path || base?.path || "/";
  const schemaType = override.schemaType || base?.schemaType || "WebPage";
  const ogImage = override.ogImage || base?.ogImage;
  const faqItems = override.faqItems;
  const breadcrumbs = override.breadcrumbs;
  const itemList = override.itemList;
  const brandName = override.brandName;
  const noindex = Boolean(override.noindex ?? base?.noindex);

  const [pathnameOnly, queryFromPath] = String(path).split("?");
  const query =
    override.query ||
    (queryFromPath ? `?${queryFromPath}` : "") ||
    "";
  const langPath = `${langPrefix(currentLanguage)}${
    pathnameOnly === "/" ? "" : pathnameOnly
  }`;
  const canonicalPath =
    override.canonicalPath || `${langPath}${query}` || "/";
  const canonicalUrl = getAbsoluteUrl(canonicalPath);
  const hreflang = buildPathHreflang(pathnameOnly || "/", query);
  const jsonLd = buildStaticPageJsonLd({
    title,
    description,
    canonicalUrl,
    lang: currentLanguage,
    path: pathnameOnly || "/",
    schemaType,
    ogImage,
    faqItems,
    breadcrumbs,
    itemList,
    brandName,
  });

  return (
    <>
      <MetaTags>
        <title>{title}</title>
        {description ? <meta name="description" content={description} /> : null}
        <link rel="canonical" href={canonicalUrl} />
        {hreflang.map((item) => (
          <link
            key={item.hreflang}
            rel="alternate"
            hrefLang={item.hreflang}
            href={item.href}
          />
        ))}
        {noindex ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : (
          <meta name="robots" content="index, follow" />
        )}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="VLV Centre" />
        <meta property="og:locale" content={ogLocale(currentLanguage)} />
        <meta property="og:title" content={title} />
        {description ? (
          <meta property="og:description" content={description} />
        ) : null}
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        {ogImage ? <meta property="og:image:alt" content={title} /> : null}
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        {description ? (
          <meta name="twitter:description" content={description} />
        ) : null}
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      </MetaTags>
      <JsonLd data={jsonLd} />
    </>
  );
}
